import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ShoppingCart, Search, ChevronDown, ChevronUp, Store } from 'lucide-react';
import api from '../../utils/api';

const B2C_STATUS_LABELS = {
  new: { label: 'Neu', color: 'bg-blue-100 text-blue-800' },
  confirmed: { label: 'Bestätigt', color: 'bg-purple-100 text-purple-800' },
  processing: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  ready_for_pickup: { label: 'Abholbereit', color: 'bg-orange-100 text-orange-800' },
  shipped: { label: 'Versendet', color: 'bg-cyan-100 text-cyan-800' },
  completed: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800' }
};

const AdminB2COrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/admin/b2c-orders');
      setOrders(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const filteredOrders = orders.filter(order => 
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.shop_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            B2C-Bestellungen ({orders.length})
          </h2>
          <p className="text-gray-500 text-sm">
            Alle Endkundenbestellungen aus allen Reseller-Shops
          </p>
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Bestellung suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium">Keine B2C-Bestellungen</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Keine Bestellungen entsprechen Ihrer Suche' : 'Noch keine Endkundenbestellungen eingegangen'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order.id} className="bg-white border border-border rounded-lg overflow-hidden">
              {/* Order Header */}
              <div 
                className="flex flex-col md:flex-row md:items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex items-center gap-4 mb-2 md:mb-0">
                  <div>
                    <div className="font-bold text-primary">{order.order_number}</div>
                    <div className="text-sm text-gray-500">{formatDate(order.created_at)}</div>
                  </div>
                  <div className="px-2 py-1 bg-gray-100 rounded text-xs flex items-center gap-1">
                    <Store className="h-3 w-3" />
                    {order.shop_name}
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <div className="font-medium">
                      {order.customer_first_name} {order.customer_last_name}
                    </div>
                    <div className="text-sm text-gray-500">{order.customer_email}</div>
                  </div>
                  
                  <div className="text-end">
                    <div className="font-bold">{formatPrice(order.total_gross)}</div>
                    <div className="text-sm text-gray-500">{order.items?.length || 0} Artikel</div>
                  </div>
                  
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${B2C_STATUS_LABELS[order.status]?.color || 'bg-gray-100'}`}>
                    {B2C_STATUS_LABELS[order.status]?.label || order.status}
                  </span>
                  
                  {expandedOrder === order.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
              
              {/* Order Details */}
              {expandedOrder === order.id && (
                <div className="border-t p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2">Kundendaten</h4>
                      <div className="text-sm space-y-1">
                        <div>{order.customer_first_name} {order.customer_last_name}</div>
                        <div>{order.customer_email}</div>
                        {order.customer_phone && <div>{order.customer_phone}</div>}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">Lieferadresse</h4>
                      <div className="text-sm space-y-1">
                        <div>{order.shipping_street}</div>
                        <div>{order.shipping_postal_code} {order.shipping_city}</div>
                        <div>{order.shipping_country}</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">Kundennotiz</h4>
                      <div className="text-sm text-gray-600">
                        {order.customer_note || '-'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Items */}
                  <div className="mt-6">
                    <h4 className="font-semibold mb-2">Bestellte Produkte</h4>
                    <div className="bg-white rounded border">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-start px-3 py-2">Produkt</th>
                            <th className="text-center px-3 py-2">Menge</th>
                            <th className="text-end px-3 py-2">Einzelpreis</th>
                            <th className="text-end px-3 py-2">Gesamt</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {order.items?.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{item.product_title_snapshot}</div>
                                {item.product_sku_snapshot && (
                                  <div className="text-xs text-gray-500">Art.-Nr.: {item.product_sku_snapshot}</div>
                                )}
                              </td>
                              <td className="text-center px-3 py-2">{item.quantity}</td>
                              <td className="text-end px-3 py-2">{formatPrice(item.unit_price_gross)}</td>
                              <td className="text-end px-3 py-2 font-medium">{formatPrice(item.line_total_gross)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t bg-gray-50">
                          <tr>
                            <td colSpan={3} className="text-end px-3 py-2">Zwischensumme</td>
                            <td className="text-end px-3 py-2">{formatPrice(order.subtotal_gross)}</td>
                          </tr>
                          <tr>
                            <td colSpan={3} className="text-end px-3 py-2">Versand</td>
                            <td className="text-end px-3 py-2">{formatPrice(order.shipping_gross)}</td>
                          </tr>
                          <tr className="font-bold">
                            <td colSpan={3} className="text-end px-3 py-2">Gesamtbetrag</td>
                            <td className="text-end px-3 py-2">{formatPrice(order.total_gross)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminB2COrders;
